import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowUpRight, Check, Sparkles, Mail, Calendar as CalendarIcon,
  MessageSquare, LineChart, ShieldCheck, Layers, Zap, Users, FileText, Lock,
  Boxes, Workflow, Building2
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const pillars = [
  { icon: Mail, title: 'Auto-captured activity', desc: 'Mail, calendar and meetings flow into contacts, companies and deals — with zero typing.' },
  { icon: Sparkles, title: 'AI drafts, humans approve', desc: 'Replies, proposals, summaries and next steps are pre-written. Your team just taps approve.' },
  { icon: Boxes, title: 'Runs on Microsoft 365', desc: 'Uses the mailboxes, calendars, Teams and files your business already pays for.' },
  { icon: LineChart, title: 'Forecasts leaders trust', desc: 'Weighted pipeline, rep-committed numbers, nightly snapshots and quota coverage.' },
  { icon: FileText, title: 'Proposals, quotes & e-sign', desc: 'Design, preview, send and get signed — with prices sourced from your catalog.' },
  { icon: ShieldCheck, title: 'Enterprise-grade by default', desc: 'Per-tenant data isolation, full audit trail, RBAC, UAE data residency.' }
];

const switchReasons = [
  { versus: 'Instead of Salesforce', note: 'Live in minutes — no admin army, no per-feature upsells, AI included (not a five-figure add-on).' },
  { versus: 'Instead of HubSpot', note: 'Not another silo bolted onto email. Lives inside Microsoft 365 with per-user pricing.' },
  { versus: 'Instead of Zoho', note: 'A modern AI-native platform — not a sprawling suite you have to assemble and babysit.' },
  { versus: 'Instead of Pipedrive', note: 'A full platform — sales, service, marketing, proposals & AI — not just a pretty pipeline.' }
];

const modules = [
  { icon: Workflow, title: 'Sales & pipeline', desc: 'Fast Kanban, AI win likelihood, buying committees, activity capture.' },
  { icon: MessageSquare, title: 'Service desk', desc: 'Tickets, SLAs and shared inboxes — connected to the same customer record.' },
  { icon: Zap, title: 'Marketing', desc: 'Segments, journeys and campaigns that read the same source of truth.' },
  { icon: FileText, title: 'Proposals & quoting', desc: 'Catalog-driven proposals, quote acceptance and online e-sign.' },
  { icon: Sparkles, title: 'Ask Zaatly', desc: 'Ask in plain English — get a governed answer plus the SQL behind it.' },
  { icon: Users, title: 'My Day', desc: 'An AI-planned day with your tasks, quota, momentum and a live pipeline snapshot.' }
];

const ZaatlyPage = () => {
  return (
    <main className="bg-[#05080f] min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative pt-36 pb-20 md:pt-44 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.18),transparent_60%)]"></div>
        <div className="perspective-grid"></div>

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-amber-300 text-sm mb-6">
            <ArrowLeft size={14} /> Back to home
          </Link>
          <div className="section-label mb-6">Product · Zaatly</div>
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
            <div>
              <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-semibold leading-[1.03] tracking-tight text-white">
                The CRM that <span className="gradient-text">fills itself in.</span>
              </h1>
              <p className="mt-6 text-slate-300 max-w-xl text-base md:text-lg leading-relaxed">
                Zaatly is an AI-native CRM built on Microsoft 365. It captures your work
                automatically and drafts the next email, proposal, or follow-up — your team
                just approves. Finally, a CRM people keep up to date because it keeps itself
                up to date.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/#contact">
                  <Button className="btn-gradient rounded-full h-11 px-6 border-0">
                    Get in touch for demo & pricing <ArrowUpRight size={16} className="ml-1" />
                  </Button>
                </Link>
                <a href="https://zaatly.com" target="_blank" rel="noreferrer">
                  <Button variant="outline" className="rounded-full h-11 px-6 bg-white/5 border-white/10 text-white hover:bg-white/10">
                    Visit zaatly.com
                  </Button>
                </a>
              </div>
            </div>

            <div className="card-dark rounded-3xl p-6 relative">
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[{v:'0',l:'minutes of data entry'},{v:'1',l:'workspace for it all'},{v:'100%',l:'per-tenant isolation'}].map((s) => (
                  <div key={s.l} className="rounded-xl bg-white/[0.03] border border-white/5 p-4 text-center">
                    <div className="font-display text-2xl gradient-text font-semibold">{s.v}</div>
                    <div className="text-slate-400 text-[11px] mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
              <div className="rounded-2xl overflow-hidden border border-white/10">
                <img
                  src="https://zaatly.com/screens/my-day.png"
                  alt="Zaatly My Day"
                  className="w-full h-auto"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
              </div>
              <p className="text-slate-400 text-xs mt-3 text-center">A real Zaatly workspace — not a mockup.</p>
            </div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="relative py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="section-label mb-6 justify-center">The problem with every other CRM</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight">
            Reps don&apos;t hate CRM. <br className="hidden md:block" />
            They hate <span className="gradient-text">feeding it.</span>
          </h2>
          <p className="text-slate-400 leading-relaxed mt-6 max-w-2xl mx-auto">
            Pipelines rot because updating them is a chore. Forecasts drift. Deals slip.
            Leaders fly blind. Zaatly flips the model — it captures and drafts, your people
            confirm. The CRM stays current because staying current is now effortless.
          </p>
        </div>
      </section>

      {/* PILLARS */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">What you get</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-14">
            One platform. <span className="gradient-text">The whole customer journey.</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {pillars.map((p) => (
              <div key={p.title} className="card-dark rounded-3xl p-6 hover:border-amber-500/30 transition-colors">
                <div className="h-12 w-12 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center mb-4">
                  <p.icon className="text-amber-400" size={22} strokeWidth={1.5} />
                </div>
                <h3 className="text-white text-lg font-semibold mb-2">{p.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">Modules included</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-12">
            Sales, service, marketing and quoting — <span className="gradient-text">intelligently connected.</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {modules.map((m) => (
              <div key={m.title} className="card-dark rounded-2xl p-5 flex items-start gap-4 hover:border-amber-500/30 transition-colors">
                <div className="h-10 w-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                  <m.icon size={18} className="text-amber-400" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-white font-semibold">{m.title}</div>
                  <div className="text-slate-400 text-sm mt-1">{m.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY SWITCH */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="section-label mb-6">Why teams switch</div>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-12">
            Everything big names charge <span className="gradient-text">extra for — included.</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            {switchReasons.map((s) => (
              <div key={s.versus} className="card-dark rounded-3xl p-7 hover:border-amber-500/30 transition-colors">
                <div className="text-amber-300 text-sm mb-2">{s.versus}</div>
                <p className="text-slate-300 text-base leading-relaxed">{s.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECURITY / TRUST */}
      <section className="relative py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="card-dark rounded-3xl p-8 md:p-10">
            <div className="flex flex-col md:flex-row gap-6 md:items-center">
              <div className="h-16 w-16 rounded-2xl bg-amber-500/10 border border-amber-500/25 flex items-center justify-center shrink-0">
                <Lock className="text-amber-400" size={26} strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="text-white text-xl font-semibold mb-2">Enterprise controls, without the enterprise tax</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Hard per-tenant data isolation, forced row-level security, a full audit trail,
                  role-based access, and optional UAE data residency — the serious controls
                  serious buyers require, priced honestly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="card-dark rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.18),transparent_60%)]"></div>
            <div className="relative">
              <Sparkles className="text-amber-300 mx-auto mb-3" size={28} />
              <h2 className="font-display text-3xl md:text-5xl font-semibold text-white tracking-tight leading-tight mb-4">
                Approve. <span className="gradient-text">Don&apos;t type.</span>
              </h2>
              <p className="text-slate-300 max-w-xl mx-auto mb-8 leading-relaxed">
                See how Zaatly can fill itself in for your team. Get a personalized demo
                and pricing tailored to your organization.
              </p>
              <Link to="/#contact">
                <Button className="btn-gradient rounded-full h-12 px-8 border-0 text-base">
                  Get in touch for demo & pricing
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default ZaatlyPage;
